import React, { useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { ArrowRightIcon, CalendarIcon } from 'lucide-react';
type Language = 'ua' | 'en';
type NewsSectionProps = {
  language: Language;
};
const news = {
  ua: [{
    id: 1,
    date: '15 січня 2024',
    title: 'CHAMALEON розширює присутність на ринку Східної Європи',
    excerpt: "Компанія підписала нові дистриб'юторські угоди з партнерами в Польщі та Чехії..."
  }, {
    id: 2,
    date: '8 січня 2024',
    title: 'Нова лінійка екологічних покриттів',
    excerpt: 'Представляємо інноваційну серію водорозчинних лаків з мінімальним вмістом VOC...'
  }, {
    id: 3,
    date: '28 грудня 2023',
    title: 'Підсумки року: рекордні показники продажів',
    excerpt: 'CHAMALEON завершує 2023 рік з найкращими результатами за останні 10 років...'
  }],
  en: [{
    id: 1,
    date: 'January 15, 2024',
    title: 'CHAMALEON Expands Presence in Eastern European Market',
    excerpt: 'The company has signed new distribution agreements with partners in Poland and Czech Republic...'
  }, {
    id: 2,
    date: 'January 8, 2024',
    title: 'New Line of Eco-Friendly Coatings',
    excerpt: 'Introducing an innovative series of water-based lacquers with minimal VOC content...'
  }, {
    id: 3,
    date: 'December 28, 2023',
    title: 'Year in Review: Record Sales Performance',
    excerpt: 'CHAMALEON concludes 2023 with the best results in the last 10 years...'
  }]
};
const sectionText = {
  ua: {
    title: 'Новини',
    viewAll: 'Усі новини',
    readMore: 'Читати'
  },
  en: {
    title: 'News',
    viewAll: 'All News',
    readMore: 'Read More'
  }
};
function NewsVisual({
  index
}: {
  index: number;
}) {
  const variants = [<div className="absolute inset-0 flex items-center justify-center" data-id="element-220">
      <motion.div animate={{
      rotate: [0, 180, 360]
    }} transition={{
      duration: 20,
      repeat: Infinity,
      ease: 'linear'
    }} className="w-28 h-28 sm:w-32 sm:h-32 border-4 border-chamaleon-yellow/30 rounded-2xl" data-id="element-221" />
      <div className="absolute w-16 h-16 sm:w-20 sm:h-20 bg-chamaleon-yellow/20 rounded-full blur-xl" data-id="element-222" />
    </div>, <div className="absolute inset-0 flex items-center justify-center" data-id="element-223">
      <motion.div animate={{
      scale: [1, 1.2, 1]
    }} transition={{
      duration: 4,
      repeat: Infinity
    }} className="w-32 h-32 sm:w-40 sm:h-40 bg-gradient-to-br from-chamaleon-yellow/30 to-transparent rounded-full blur-2xl" data-id="element-224" />
      <div className="absolute w-20 h-20 sm:w-24 sm:h-24 border-2 border-white/10 rounded-full" data-id="element-225" />
    </div>, <div className="absolute inset-0 flex items-center justify-center overflow-hidden" data-id="element-226">
      <div className="absolute inset-0" style={{
      backgroundImage: `repeating-linear-gradient(
          45deg,
          rgba(255,184,28,0.1),
          rgba(255,184,28,0.1) 2px,
          transparent 2px,
          transparent 12px
        )`
    }} data-id="element-227" />
      <div className="w-28 h-28 sm:w-32 sm:h-32 bg-chamaleon-yellow/20 rounded-2xl backdrop-blur-xl" data-id="element-228" />
    </div>];
  return variants[index % 3];
}
export function NewsSection({
  language
}: NewsSectionProps) {
  const ref = useRef(null);
  const isInView = useInView(ref, {
    once: true,
    margin: '-100px'
  });
  const t = sectionText[language];
  const newsList = news[language];
  return <section ref={ref} className="relative py-16 sm:py-20 lg:py-32 bg-chamaleon-black overflow-hidden" data-id="element-229">
      <div className="absolute inset-0 opacity-[0.02]" style={{
      backgroundImage: `linear-gradient(rgba(255,255,255,0.1) 1px, transparent 1px),
                         linear-gradient(90deg, rgba(255,255,255,0.1) 1px, transparent 1px)`,
      backgroundSize: '100px 100px'
    }} data-id="element-230" />

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-231">
        {/* Header */}
        <motion.div initial={{
        opacity: 0,
        y: 30
      }} animate={isInView ? {
        opacity: 1,
        y: 0
      } : {}} transition={{
        duration: 0.6
      }} className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-12" data-id="element-232">
          <div data-id="element-233">
            <h2 className="text-3xl sm:text-4xl lg:text-6xl font-bold text-white mb-4" data-id="element-234">
              {t.title}
            </h2>
            <div className="w-20 h-0.5 bg-gradient-to-r from-chamaleon-yellow to-transparent" data-id="element-235" />
          </div>
          <motion.a href="#" className="inline-flex items-center gap-2 text-chamaleon-yellow font-medium group text-sm sm:text-base pb-1" whileHover={{
          x: 5
        }} data-id="element-236">
            {t.viewAll}
            <ArrowRightIcon className="w-4 h-4 sm:w-5 sm:h-5 group-hover:translate-x-1 transition-transform" data-id="element-237" />
          </motion.a>
        </motion.div>

        {/* News Grid */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6" data-id="element-238">
          {newsList.map((item, index) => <motion.a key={item.id} href="#" initial={{
          opacity: 0,
          y: 30
        }} animate={isInView ? {
          opacity: 1,
          y: 0
        } : {}} transition={{
          duration: 0.6,
          delay: index * 0.1
        }} whileHover={{
          y: -8
        }} className="group block" data-id="element-239">
              <div className="glass-card rounded-xl sm:rounded-2xl overflow-hidden h-full hover:border-chamaleon-yellow/30 transition-all duration-300" data-id="element-240">
                {/* Abstract Visual */}
                <div className="relative h-40 sm:h-48 bg-gradient-to-br from-chamaleon-charcoal to-chamaleon-black overflow-hidden" data-id="element-241">
                  <NewsVisual index={index} data-id="element-242" />
                  <div className="absolute inset-0 bg-gradient-to-t from-chamaleon-yellow/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" data-id="element-243" />
                </div>

                {/* Content */}
                <div className="p-4 sm:p-6" data-id="element-244">
                  <div className="flex items-center gap-2 text-gray-500 text-xs sm:text-sm mb-3" data-id="element-245">
                    <CalendarIcon className="w-3 h-3 sm:w-4 sm:h-4" data-id="element-246" />
                    {item.date}
                  </div>

                  <h3 className="text-lg sm:text-xl font-semibold text-white mb-2 sm:mb-3 transition-colors line-clamp-2" data-id="element-247">
                    {item.title}
                  </h3>

                  <p className="text-gray-400 text-sm mb-3 sm:mb-4 line-clamp-3" data-id="element-248">
                    {item.excerpt}
                  </p>

                  <span className="inline-flex items-center gap-2 text-chamaleon-yellow font-medium text-sm" data-id="element-249">
                    {t.readMore}
                    <ArrowRightIcon className="w-4 h-4 group-hover:translate-x-1 transition-transform" data-id="element-250" />
                  </span>
                </div>
              </div>
            </motion.a>)}
        </div>
      </div>
    </section>;
}